// src/components/eframes/EFrame213.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent } from "../ui/ResponsiveLayout";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import { Card } from "../ui/";
import { DocumentsIcon } from "../ui/Icons";
import type { AppDocument } from "../../types/document";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface Property {
  id: string;
  type: "house" | "apartment" | "land" | "commercial" | "other";
  address: string;
  currentValue: number;
  mortgageBalance: number;
  notes?: string;
  documents?: AppDocument[];
}

interface TaxDocument {
  id: string;
  type: "federal" | "state" | "property" | "other";
  year: string;
  description: string;
  status: "filed" | "pending" | "extension";
  amount?: number;
  documents: AppDocument[];
  notes?: string;
}

/** Backup / session-derived rows for executor inventory summary. */
interface FinancialAnswersRow {
  id?: string;
  name?: string;
  type?: string;
  institution?: string;
  payerInstitution?: string;
  address?: string;
  description?: string;
  accountNumber?: string;
  documents?: unknown;
  account?: string;
  year?: string | number;
  status?: string;
  sourceName?: string;
  frequency?: string;
  approximateAmount?: number;
  notes?: string;
  currentValue?: number;
  mortgageBalance?: number;
}

interface IncomeSource {
  id: string;
  type: string;
  sourceName: string;
  payerInstitution: string;
  frequency: string;
  approximateAmount?: number;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const EFrame213: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const loadCashAccounts = () => {
    const answers = session.taskProgress?.["financial_assets"]?.answers;
    return (answers?.cashAccounts ?? []).map((account: FinancialAnswersRow) => ({
      ...account,
      documents: normalizeDocuments(account.documents || []),
    }));
  };

  const loadInvestmentAccounts = () => {
    const answers = session.taskProgress?.["financial_assets"]?.answers;
    return (answers?.investmentAccounts ?? []).map((account: FinancialAnswersRow) => ({
      ...account,
      documents: normalizeDocuments(account.documents || []),
    }));
  };

  const loadRetirementAccounts = () => {
    const answers = session.taskProgress?.["financial_assets"]?.answers;
    return (answers?.retirementAccounts ?? []).map((account: FinancialAnswersRow) => ({
      ...account,
      documents: normalizeDocuments(account.documents || []),
    }));
  };

  const loadOtherFinancialAssets = () => {
    const answers = session.taskProgress?.["financial_assets"]?.answers;
    return (answers?.otherFinancialAssets ?? []).map((asset: FinancialAnswersRow) => ({
      ...asset,
      documents: normalizeDocuments(asset.documents || []),
    }));
  };

  const loadIncomeSources = (): IncomeSource[] => {
    const answers = session.taskProgress?.["financial_assets"]?.answers;
    return (answers?.incomeSources ?? []).map((source: IncomeSource) => ({
      ...source,
      documents: normalizeDocuments(source.documents || []),
    }));
  };

  const loadProperties = () => {
    const props = session.taskProgress?.["properties"]?.answers?.properties || [];
    return props.map((p: FinancialAnswersRow) => ({
      ...p,
      documents: normalizeDocuments(p.documents || []),
    }));
  };

  const loadTaxDocuments = () => {
    const docs = session.taskProgress?.["tax_documents"]?.answers?.documents || [];
    return docs.map((doc: FinancialAnswersRow) => ({
      ...doc,
      documents: normalizeDocuments(doc.documents || []),
    }));
  };

  const renderDocuments = (docs: AppDocument[] = [], itemId: string) => {
    const normalizedDocs = normalizeDocuments(docs);
    if (!normalizedDocs || normalizedDocs.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        {normalizedDocs.map((doc, index) => (
          <div
            key={`${itemId}-doc-${doc.id || index}-${index}`}
            className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
          >
            <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

            <span
              className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
              title={doc.name}
            >
              {doc.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const cashAccounts = loadCashAccounts();
  const investmentAccounts = loadInvestmentAccounts();
  const retirementAccounts = loadRetirementAccounts();
  const otherFinancialAssets = loadOtherFinancialAssets();
  const incomeSources = loadIncomeSources();
  const properties: Property[] = loadProperties();
  const taxDocuments: TaxDocument[] = loadTaxDocuments();

  const groupAccountsByInstitution = <T extends FinancialAnswersRow>(accounts: T[]) => {
    return accounts.reduce<Record<string, T[]>>((acc, account) => {
      const institution =
        account.institution ||
        account.payerInstitution ||
        account.address ||
        account.type ||
        account.description ||
        "Unknown Institution";

      if (!acc[institution]) acc[institution] = [];
      acc[institution].push(account);

      return acc;
    }, {});
  };

  const formatType = (value?: string) => {
    if (!value) return "N/A";
    return value.replace(/_/g, " ");
  };

  const formatFrequency = (value?: string) => {
    if (!value) return "N/A";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <div className="w-full space-y-6">
          <h1 className="text-cyan-900 text-3xl font-semibold font-['Test_Domaine_Display'] mb-8">
            {planOwnerName}'s Inventory of Assets Summary
          </h1>

          <Card className="w-full p-6">
            <h3 className="text-lg font-semibold text-stone-800 mb-6">
              Assets Overview
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">
                  {cashAccounts.length +
                    investmentAccounts.length +
                    retirementAccounts.length}
                </div>
                <div className="text-sm text-stone-500 mt-1">
                  Total Accounts
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {properties.length}
                </div>
                <div className="text-sm text-stone-500 mt-1">Properties</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {taxDocuments.length}
                </div>
                <div className="text-sm text-stone-500 mt-1">Tax Returns</div>
              </div>
            </div>
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Cash and Deposit Accounts
              </h3>
            </div>

            {cashAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(cashAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.name}
                          </div>
                          <div className="text-sm text-stone-600">
                            Type: {acc.type?.replace("_", " ")} • Ending:{" "}
                            {acc.accountNumber?.slice(-4) || "N/A"}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No cash accounts available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Investment Accounts
              </h3>
            </div>

            {investmentAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(investmentAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.name}
                          </div>
                          <div className="text-sm text-stone-600">
                            Type: {acc.type?.replace("_", " ")} • Ending:{" "}
                            {acc.accountNumber?.slice(-4) || "N/A"}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No investment accounts available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Retirement Accounts
              </h3>
            </div>

            {retirementAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(retirementAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.name}
                          </div>
                          <div className="text-sm text-stone-600">
                            Type: {acc.type?.replace("_", " ")} • Ending:{" "}
                            {acc.accountNumber?.slice(-4) || "N/A"}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No retirement accounts available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Other Financial Assets
              </h3>
            </div>

            {otherFinancialAssets.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(otherFinancialAssets)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.name}
                          </div>
                          <div className="text-sm text-stone-600">
                            Type: {acc.type?.replace("_", " ")} • Ending:{" "}
                            {acc.accountNumber?.slice(-4) || "N/A"}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No other financial assets available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Income Sources
              </h3>
            </div>

            {incomeSources.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(incomeSources)).map(
                  ([inst, sources]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {sources.map((source: IncomeSource) => (
                        <div key={source.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {source.sourceName}
                          </div>

                          <div className="text-sm text-stone-600">
                            Type: {formatType(source.type)} • Frequency:{" "}
                            {formatFrequency(source.frequency)}
                            {typeof source.approximateAmount === "number" &&
                              ` • Approx. Amount: $${source.approximateAmount.toLocaleString()}`}
                          </div>

                          {source.notes && (
                            <div className="text-sm text-stone-600 mt-2">
                              {source.notes}
                            </div>
                          )}

                          {renderDocuments(source.documents, source.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No income sources available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Properties
              </h3>
            </div>

            {properties.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(properties)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.address}
                          </div>
                          <div className="text-sm text-stone-600">
                            Type: {acc.type}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No properties available.
                </p>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Tax Returns
              </h3>
            </div>

            {taxDocuments.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(taxDocuments)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.description} ({acc.year})
                          </div>
                          <div className="text-sm text-stone-600">
                            Status: {acc.status}
                          </div>
                          {renderDocuments(acc.documents, acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm">
                  No tax returns available.
                </p>
              </div>
            )}
          </Card>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame213;